import networkSnapshot from
  "../../data/overrides/transit/vilnius-public-transport-network.json" with {
    type: "json",
  };

import {
  VILNIUS_PUBLIC_TRANSPORT_STOP_SOURCES,
  type VilniusPublicTransportMode,
  type VilniusPublicTransportStopSource,
} from "./busStops.ts";

export interface PublicTransportRouteShapePoint {
  readonly x: number;
  readonly z: number;
  readonly distanceM: number;
}

export interface PublicTransportPatternStop {
  readonly stopId: string;
  readonly position: Readonly<{ x: number; z: number }>;
  readonly distanceAlongShapeM: number;
  readonly shapeOffsetM: number;
  readonly arrivalOffsetSeconds: number;
  readonly departureOffsetSeconds: number;
}

export interface PublicTransportRoutePattern {
  readonly id: string;
  readonly sourceRouteId: string;
  readonly sourceShapeId: string;
  readonly directionId: 0 | 1;
  readonly headsign: string;
  readonly directionName: string;
  readonly mode: VilniusPublicTransportMode;
  readonly routeShortName: string;
  readonly routeLongName: string;
  readonly routeColor: string;
  readonly routeTextColor: string;
  readonly shape: readonly Readonly<PublicTransportRouteShapePoint>[];
  readonly stops: readonly Readonly<PublicTransportPatternStop>[];
  readonly departuresSeconds: readonly number[];
  readonly durationSeconds: number;
}

export interface PublicTransportRoute {
  readonly id: string;
  readonly sourceRouteId: string;
  readonly mode: VilniusPublicTransportMode;
  readonly shortName: string;
  readonly longName: string;
  readonly color: string;
  readonly textColor: string;
  readonly patterns: readonly Readonly<PublicTransportRoutePattern>[];
}

interface PublicTransportNetworkSnapshot {
  readonly schemaVersion: number;
  readonly id: string;
  readonly source: Readonly<{
    name: string;
    publisher: string;
    catalogUrl: string;
    url: string;
    termsUrl: string;
    fetchedAt: string;
    serviceDate: string;
    archiveFileName: string;
    archiveSha256: string;
    notes: string;
  }>;
  readonly localOriginLks94: Readonly<{
    crs: string;
    easting: number;
    northing: number;
    elevationM: number;
  }>;
  readonly routeCount: number;
  readonly patternCount: number;
  readonly departureCount: number;
  readonly roadAlignment?: Readonly<{
    sourceId: string;
    method: string;
    inspectedPointCount: number;
    correctedPointCount: number;
    alignedPatternCount: number;
    retainedPatternCount: number;
    retainedPatternIds: readonly string[];
    maximumOriginalOverflowM: number;
    vehicleHalfWidthM: number;
    roadEdgeClearanceM: number;
  }>;
  readonly routes: readonly Readonly<{
    id: string;
    sourceRouteId: string;
    mode: VilniusPublicTransportMode;
    shortName: string;
    longName: string;
    color: string;
    textColor: string;
    patterns: readonly Readonly<{
      id: string;
      sourceRouteId: string;
      sourceShapeId: string;
      directionId: number;
      headsign: string;
      directionName: string;
      mode: VilniusPublicTransportMode;
      routeShortName: string;
      routeLongName: string;
      routeColor: string;
      routeTextColor: string;
      shape: readonly (readonly [number, number, number])[];
      stops: readonly PublicTransportPatternStop[];
      departuresSeconds: readonly number[];
    }>[];
  }>[];
}

const snapshot = networkSnapshot as unknown as PublicTransportNetworkSnapshot;
const stopById = new Map(
  VILNIUS_PUBLIC_TRANSPORT_STOP_SOURCES.map((stop) => [stop.id, stop]),
);

function assertFinite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`${label} must be finite`);
}

function assertStrictlyIncreasing(
  values: readonly number[],
  label: string,
): void {
  for (let index = 1; index < values.length; index += 1) {
    if (values[index]! <= values[index - 1]!) {
      throw new Error(`${label} must be strictly increasing`);
    }
  }
}

function assertNondecreasing(
  values: readonly number[],
  label: string,
): void {
  for (let index = 1; index < values.length; index += 1) {
    if (values[index]! < values[index - 1]!) {
      throw new Error(`${label} must be nondecreasing`);
    }
  }
}

function expectedLocalStop(
  stop: Readonly<VilniusPublicTransportStopSource>,
): Readonly<{ x: number; z: number }> {
  return {
    x: stop.lks94.easting - snapshot.localOriginLks94.easting,
    z: snapshot.localOriginLks94.northing - stop.lks94.northing,
  };
}

function requireSnapshot(): void {
  if (
    snapshot.schemaVersion !== 1 ||
    snapshot.routeCount !== snapshot.routes.length ||
    snapshot.localOriginLks94.crs !== "EPSG:3346" ||
    !/^[0-9a-f]{64}$/u.test(snapshot.source.archiveSha256) ||
    !/^\d{8}$/u.test(snapshot.source.serviceDate)
  ) {
    throw new Error("Invalid Vilnius public-transport network source snapshot");
  }
  const routeIds = new Set<string>();
  const patternIds = new Set<string>();
  let patternCount = 0;
  let departureCount = 0;
  for (const route of snapshot.routes) {
    if (
      routeIds.has(route.id) ||
      !route.id.startsWith("judu:route:") ||
      (route.mode !== "bus" && route.mode !== "trolleybus") ||
      !route.shortName ||
      route.patterns.length !== 2 ||
      new Set(route.patterns.map((pattern) => pattern.directionId)).size !== 2
    ) {
      throw new Error(`Invalid Vilnius public-transport route ${route.id}`);
    }
    routeIds.add(route.id);
    for (const pattern of route.patterns) {
      patternCount += 1;
      departureCount += pattern.departuresSeconds.length;
      if (
        patternIds.has(pattern.id) ||
        !pattern.id.startsWith("judu:pattern:") ||
        pattern.sourceRouteId !== route.sourceRouteId ||
        pattern.mode !== route.mode ||
        pattern.routeShortName !== route.shortName ||
        (pattern.directionId !== 0 && pattern.directionId !== 1) ||
        pattern.shape.length < 2 ||
        pattern.stops.length < 2 ||
        pattern.departuresSeconds.length === 0
      ) {
        throw new Error(`Invalid Vilnius public-transport pattern ${pattern.id}`);
      }
      patternIds.add(pattern.id);
      const shapeDistances = pattern.shape.map((point) => point[2]);
      pattern.shape.flat().forEach((value) =>
        assertFinite(value, `${pattern.id} shape value`)
      );
      if (Math.abs(shapeDistances[0] ?? 1) > 0.001) {
        throw new Error(`${pattern.id} shape must start at zero metres`);
      }
      assertStrictlyIncreasing(
        shapeDistances,
        `${pattern.id} shape distances`,
      );
      assertNondecreasing(
        pattern.stops.map((stop) => stop.distanceAlongShapeM),
        `${pattern.id} stop distances`,
      );
      assertNondecreasing(
        pattern.stops.map((stop) => stop.arrivalOffsetSeconds),
        `${pattern.id} stop arrival offsets`,
      );
      assertNondecreasing(
        pattern.stops.map((stop) => stop.departureOffsetSeconds),
        `${pattern.id} stop departure offsets`,
      );
      assertStrictlyIncreasing(
        pattern.departuresSeconds,
        `${pattern.id} departures`,
      );
      for (const stop of pattern.stops) {
        const source = stopById.get(stop.stopId);
        if (source === undefined) {
          throw new Error(`${pattern.id} references missing stop ${stop.stopId}`);
        }
        const expected = expectedLocalStop(source);
        if (
          Math.hypot(
            expected.x - stop.position.x,
            expected.z - stop.position.z,
          ) > 0.002 ||
          stop.shapeOffsetM < 0 ||
          stop.shapeOffsetM > 40.001 ||
          !source.routeShortNames.includes(route.shortName)
        ) {
          throw new Error(`${pattern.id} has invalid stop ${stop.stopId}`);
        }
      }
    }
  }
  if (
    patternCount !== snapshot.patternCount ||
    departureCount !== snapshot.departureCount
  ) {
    throw new Error("Vilnius public-transport network counts do not match");
  }
}

requireSnapshot();

export const VILNIUS_PUBLIC_TRANSPORT_NETWORK_SOURCE = Object.freeze({
  id: snapshot.id,
  ...snapshot.source,
  routeCount: snapshot.routeCount,
  patternCount: snapshot.patternCount,
  departureCount: snapshot.departureCount,
  roadAlignment: snapshot.roadAlignment === undefined
    ? null
    : Object.freeze({ ...snapshot.roadAlignment }),
});

export const VILNIUS_PUBLIC_TRANSPORT_ROUTES:
  readonly Readonly<PublicTransportRoute>[] = Object.freeze(
    snapshot.routes.map((route) => Object.freeze({
      ...route,
      patterns: Object.freeze(route.patterns.map((pattern) => Object.freeze({
        ...pattern,
        directionId: pattern.directionId as 0 | 1,
        shape: Object.freeze(pattern.shape.map(([x, z, distanceM]) =>
          Object.freeze({ x, z, distanceM })
        )),
        stops: Object.freeze(pattern.stops.map((stop) => Object.freeze({
          ...stop,
          position: Object.freeze({ ...stop.position }),
        }))),
        departuresSeconds: Object.freeze([...pattern.departuresSeconds]),
        durationSeconds:
          pattern.stops.at(-1)?.departureOffsetSeconds ?? 0,
      }))),
    })),
  );

export const VILNIUS_PUBLIC_TRANSPORT_PATTERNS:
  readonly Readonly<PublicTransportRoutePattern>[] = Object.freeze(
    VILNIUS_PUBLIC_TRANSPORT_ROUTES.flatMap((route) => route.patterns),
  );

const patternById = new Map(
  VILNIUS_PUBLIC_TRANSPORT_PATTERNS.map((pattern) => [pattern.id, pattern]),
);

export function publicTransportPatternById(
  id: string,
): Readonly<PublicTransportRoutePattern> | null {
  return patternById.get(id) ?? null;
}

export function publicTransportStopById(
  id: string,
): Readonly<VilniusPublicTransportStopSource> | null {
  return stopById.get(id) ?? null;
}
